// CalendarPopup.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { normalizeDateValue } from 'utilities/DateTime';

// CSS
import './CalendarPopup.css';

// Icons
import Icon from 'svg/Icon';

// Components
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isSameDay,
  isToday,
  startOfMonth,
  subMonths,
} from 'date-fns';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/**
 * Builds a calendar grid for a given month, including leading and trailing empty cells 
 * to align days correctly.
 * 
 * @param {Date} monthDate - A date object representing any day within the target month.
 * 
 * @returns {Array<Date|null>} An array of date objects for each day in the month, with nulls for empty cells.
 */
const buildMonthGrid = (monthDate) => {
  const start = startOfMonth(monthDate);
  const end = endOfMonth(monthDate);
  const days = eachDayOfInterval({ start, end });

  const leading = getDay(start);
  const leadingCells = Array.from({ length: leading }, () => null);

  const cells = [...leadingCells, ...days];
  const remainder = cells.length % 7;
  if (remainder !== 0) {
    const trailing = 7 - remainder;
    for (let i = 0; i < trailing; i += 1) {
      cells.push(null);
    }
  }

  return cells;
};

/**
 * CalendarPopup
 *
 * Renders a month calendar popup with date selection and hover/selected states.
 *
 * @param {Date|string|number|null} value - Current selected date value.
 * @param {function} onSelect - Callback fired when a day is selected.
 *
 * @returns {ReactNode} The rendered component.
 */
const CalendarPopup = ({ value, onSelect }) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // Normalized selected date used to set initial month.
  const normalized = normalizeDateValue(value) || new Date();

  // ----- STATE ----- //

  // Current visible month.

  const [currentMonth, setCurrentMonth] = useState(startOfMonth(normalized));

  // Hovered day ISO key for hover state.
  const [hoveredDayKey, setHoveredDayKey] = useState(null);

  // ----- MEMOS ----- //

  // Calendar grid cells for current month.

  const cells = useMemo(() => buildMonthGrid(currentMonth), [currentMonth]);

  // Normalized selected day value.
  const selectedDate = normalizeDateValue(value);

  // ----- EFFECTS ----- //

  // Sync visible month when incoming selected date changes.

  useEffect(() => {
    const next = normalizeDateValue(value);
    if (next) {
      setCurrentMonth(startOfMonth(next));
    }
  }, [value]);

  // ----- UTILITIES ----- //

  /**
   * Stops click/mousedown events from escaping the popup.
   *
   * @param {MouseEvent} event - Browser event object.
   */

  const stopEvent = (event) => {
    event.stopPropagation();
  };

  // ----- RENDER ----- //

  return (
    <div
      className="calendar-popup-container"
      onClick={stopEvent}
      onMouseDown={stopEvent}
    >
      {/* HEADER */}
      <div className="calendar-popup-header">

        {/* NAVIGATION BUTTON */}
        <button
          className="calendar-popup-nav"
          type="button"
          aria-label="Previous month"
          onClick={() => setCurrentMonth((prev) => subMonths(prev, 1))}
        >
          <Icon
            iconKey="chevron_left"
            width={18}
            height={18}
            color={theme.foreground}
          />
        </button>

        {/* TITLE */}
        <div className="calendar-popup-title">
          {format(currentMonth, 'MMMM yyyy')}
        </div>

        {/* NAVIGATION BUTTON */}
        <button
          className="calendar-popup-nav"
          type="button"
          aria-label="Next month"
          onClick={() => setCurrentMonth((prev) => addMonths(prev, 1))}
        >
          <Icon
            iconKey="chevron_right"
            width={18}
            height={18}
            color={theme.foreground}
          />
        </button>
      </div>

      {/* WEEKDAYS */}
      <div className="calendar-popup-weekdays">
        {WEEKDAYS.map((label, index) => (
          <div
            className="calendar-popup-weekday"
            key={`${label}-${index}`}
          >
            {label}
          </div>
        ))}
      </div>

      {/* GRID */}
      <div className="calendar-popup-grid">
        {cells.map((day, index) => {
          if (!day) {

            return <div className="calendar-popup-cell empty" key={`empty-${index}`} />;
          }

          const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
          const isTodayDate = isToday(day);

          const className = [
            'calendar-popup-cell',
            isSelected ? 'selected' : '',
            isTodayDate ? 'today' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              className={className}
              style={{
                background: isSelected
                  ? theme.highlight
                  : hoveredDayKey === day.toISOString()
                    ? theme.backgroundHover
                    : 'transparent',
                borderColor: isTodayDate ? theme.foregroundMuted : 'transparent',
                color: isSelected ? theme.highlightForeground : theme.foreground,
              }}
              key={day.toISOString()}
              type="button"
              onClick={() => onSelect?.(day)}
              onMouseEnter={() => setHoveredDayKey(day.toISOString())}
              onMouseLeave={() => setHoveredDayKey(null)}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Dev Tools
CalendarPopup.displayName = 'CalendarPopup';

export default CalendarPopup;
